import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

/**
 * QR scanner button — opens a modal with the device camera and scans QR codes.
 * On scan, it extracts the URL and navigates (or calls onScanned(payload)).
 */
export function QRScannerButton({ onScanned, className = '' }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const html5Ref = useRef(null);
  const containerId = 'qr-scanner-region';

  useEffect(() => {
    if (!open) return undefined;
    let running = true;
    let scanner = null;

    (async () => {
      // html5-qrcode is ~1 MB of JS.  It used to be a static import, which put
      // it in the main bundle and made EVERY cold page load pay for a camera
      // library almost nobody opens.  Fetch it the first time the scanner is
      // actually opened instead.
      let Html5Qrcode;
      try {
        ({ Html5Qrcode } = await import('html5-qrcode'));
      } catch {
        if (!running) return;
        toast.error('Scanner failed to load — check your connection and retry');
        setOpen(false);
        return;
      }
      if (!running) return;
      scanner = new Html5Qrcode(containerId, { verbose: false });
      html5Ref.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (!running) return;
            running = false;
            scanner.stop().then(() => scanner.clear()).catch(() => {});
            setOpen(false);
            if (onScanned) return onScanned(decoded);
            // Default: navigate to the URL if it belongs to this origin
            try {
              const url = new URL(decoded);
              if (url.origin === window.location.origin) {
                navigate(url.pathname + url.search + url.hash);
                return;
              }
              // Different origin: just open in new tab
              window.open(decoded, '_blank');
            } catch {
              toast.info(`Scanned: ${decoded}`);
            }
          },
          () => { /* silent: ignore per-frame errors */ }
        );
      } catch (e) {
        if (!running) return;
        toast.error('Unable to access camera');
        setOpen(false);
      }
    })();

    return () => {
      running = false;
      // The instance may not exist yet (closed while the library was still
      // downloading) — that is why this is guarded rather than assumed.
      const s = scanner || html5Ref.current;
      html5Ref.current = null;
      if (!s) return;
      try {
        s.stop().then(() => s.clear()).catch(() => {});
      } catch { /* ignore */ }
    };
  }, [open, onScanned, navigate]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className={className}
        data-testid="qr-scanner-btn"
      >
        <Camera className="h-4 w-4 mr-1.5" weight="bold" />
        Scan QR
      </Button>
      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative rounded-lg p-4 w-[min(92vw,440px)]"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 p-1 rounded"
              style={{ color: 'var(--text-secondary)' }}
              aria-label="close"
              data-testid="qr-scanner-close"
            >
              <X className="h-4 w-4" weight="bold" />
            </button>
            <div
              className="text-[13px] uppercase tracking-[0.12em] mb-2"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}
            >
              Scan a QR Code
            </div>
            <div id={containerId} style={{ width: '100%', minHeight: 300, background: '#000', borderRadius: 6 }} />
            <div className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
              Aim the camera at a heat / TC / RM-issue QR. The app will jump to its traceability view automatically.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QRScannerButton;
